import { FALLBACK_BASELINE_RATIO } from '@/fonts/baselineRatio';
import { FontMatchMap, resolveEmbeddedItemFont } from '@/fonts/fontMatching';
import { calculateRenderedTextItemProps } from '@/services/PdfService';
import {
  Annotation,
  Coordinates,
  EditableTextItem,
  FormField,
  ImageItem,
  PageTextItems,
  Paths,
  PDFJSTextItem,
  Point,
  TextCoordinates,
} from '@/types';
import * as pdfjs from 'pdfjs-dist';
import { v4 as uuidv4 } from 'uuid';
import { rgbToHex } from './colors';
import { rotateImage } from './utils';
import { COORDINATES_PIXELS_EPSILON } from '@/constants/constants';
import { Shape } from '@/types/shapes';
import { PageMeta } from '@/components/PdfEditor/rendering/DocumentMeta';

const getCanvasScales = (page: PageMeta, scale: number) => {
  const { width, height } = page.getViewport({ scale: 1, rotation: 0 });
  const { width: scaledWidth, height: scaledHeight } = page.getViewport({ scale, rotation: 0 });

  const scaleX = scaledWidth / width;
  const scaleY = scaledHeight / height;

  return { scaleX, scaleY };
};

function scaleTextBoxItemToCanvas(textItem: EditableTextItem, page: PageMeta, scale: number): EditableTextItem {
  return {
    ...textItem,
    originalCanvasCoordinates: pdfCoordinatesToCanvasCoordinates(
      textItem.originalPdfCoordinates,
      page,
      scale,
    ) as TextCoordinates,
    currentCanvasCoordinates: pdfCoordinatesToCanvasCoordinates(
      textItem.currentPdfCoordinates,
      page,
      scale,
    ) as TextCoordinates,
  };
}

function pdfCoordinatesToCanvasCoordinates(
  pdfCoordinates: TextCoordinates,
  page: PageMeta,
  scale: number,
  adjustForBoundingBox?: boolean,
  flipBoxDimensions?: boolean,
  accountForPageRotation?: boolean,
): TextCoordinates;
function pdfCoordinatesToCanvasCoordinates(
  pdfCoordinates: Coordinates,
  page: PageMeta,
  scale: number,
  adjustForBoundingBox?: boolean,
  flipBoxDimensions?: boolean,
  accountForPageRotation?: boolean,
): Coordinates;
function pdfCoordinatesToCanvasCoordinates(
  pdfCoordinates: Coordinates | TextCoordinates,
  page: PageMeta,
  scale: number,
  adjustForBoundingBox: boolean = false,
  flipBoxDimensions: boolean = true, // Flip width and height
  accountForPageRotation: boolean = true,
): Coordinates | TextCoordinates {
  const { width: pdfPageWidth, height: pdfPageHeight, viewBox } = page.getViewport({ scale: 1, rotation: 0 });

  const pageYOffset = viewBox[1];

  const { scaleX, scaleY } = getCanvasScales(page, scale);

  const { x, y, width, height } = pdfCoordinates;

  const processedPdfCoordinates = {
    x: x * scaleX,
    y: (pdfPageHeight - y + pageYOffset) * scaleY,
    width: width * scaleX,
    height: height * scaleY,
    ...('fontSize' in pdfCoordinates ? { fontSize: pdfCoordinates.fontSize * scaleY } : {}),
  };

  if (accountForPageRotation && page.rotate !== 0) {
    const canvasWidth = pdfPageWidth * scale;
    const canvasHeight = pdfPageHeight * scale;
    return rotateCoordinates(
      processedPdfCoordinates,
      page.rotate,
      canvasWidth,
      canvasHeight,
      adjustForBoundingBox,
      flipBoxDimensions,
    );
  }

  return processedPdfCoordinates;
}

function canvasCoordinatesToPdfCoordinates(
  canvasCoordinates: TextCoordinates,
  page: PageMeta,
  scale: number,
  adjustForBoundingBox?: boolean,
): TextCoordinates;
function canvasCoordinatesToPdfCoordinates(
  canvasCoordinates: Coordinates,
  page: PageMeta,
  scale: number,
  adjustForBoundingBox?: boolean,
): Coordinates;
function canvasCoordinatesToPdfCoordinates(
  canvasCoordinates: Coordinates | TextCoordinates,
  page: PageMeta,
  scale: number,
  adjustForBoundingBox: boolean = false,
): Coordinates | TextCoordinates {
  const { height: pdfPageHeight, viewBox } = page.getViewport({ scale: 1, rotation: 0 });

  const pageYOffset = viewBox[1];

  const { scaleX, scaleY } = getCanvasScales(page, scale);

  let processedCanvasCoordinates = canvasCoordinates;

  if (page.rotate !== 0) {
    const { width: canvasWidth, height: canvasHeight } = page.getViewport({ scale });
    processedCanvasCoordinates = rotateCoordinates(
      canvasCoordinates,
      -page.rotate,
      canvasWidth,
      canvasHeight,
      adjustForBoundingBox,
    );
  }

  const { x, y, width, height } = processedCanvasCoordinates;

  return {
    x: x / scaleX,
    y: pdfPageHeight - y / scaleY + pageYOffset,
    width: width / scaleX,
    height: height / scaleY,
    ...('fontSize' in canvasCoordinates ? { fontSize: canvasCoordinates.fontSize / scaleY } : {}),
  };
}

function pdfPointsToCanvasPoints(pdfPoints: Point[], page: PageMeta, scale: number): Point[] {
  const { width: pdfPageWidth, height: pdfPageHeight, viewBox } = page.getViewport({ scale: 1 });

  const pageYOffset = viewBox[1];

  const scaleX = page.getViewport({ scale }).width / pdfPageWidth;
  const scaleY = page.getViewport({ scale }).height / pdfPageHeight;

  return pdfPoints.map((point) => ({
    x: point.x * scaleX,
    y: (pdfPageHeight - point.y + pageYOffset) * scaleY,
  }));
}

function canvasPointsToPdfPoints(canvasCoordinates: Point[], page: PageMeta, scale: number): Point[] {
  const { width: pdfPageWidth, height: pdfPageHeight, viewBox } = page.getViewport({ scale: 1 });

  const pageYOffset = viewBox[1];

  const scaleX = page.getViewport({ scale }).width / pdfPageWidth;
  const scaleY = page.getViewport({ scale }).height / pdfPageHeight;

  return canvasCoordinates.map((point) => ({
    x: point.x / scaleX,
    y: pdfPageHeight - point.y / scaleY + pageYOffset,
  }));
}

export const coordinatesToPoints = (coordinates: Coordinates): Point[] => {
  const { x, y, width, height } = coordinates;

  return [
    { x, y },
    { x: x + width, y },
    { x: x + width, y: y + height },
    { x, y: y + height },
  ];
};

export const pointsToCoordinates = (points: Point[]): Coordinates => {
  const result = { x: 0, y: 0, width: 0, height: 0 };

  if (points.length !== 4) {
    //console.warn("Number of points provided to pointsToCoordinates is not 4");
    result.x = Math.min(...points.map((p) => p.x));
    result.y = Math.min(...points.map((p) => p.y));
    result.width = Math.max(...points.map((p) => p.x)) - result.x;
    result.height = Math.max(...points.map((p) => p.y)) - result.y;
  } else {
    result.x = Math.min(points[0].x, points[2].x);
    result.y = Math.min(points[0].y, points[2].y);
    result.width = Math.abs(points[0].x - points[2].x);
    result.height = Math.abs(points[0].y - points[2].y);
  }

  return result;
};

export const normalizeRotation = (rotation: number) => ((rotation % 360) + 360) % 360;

export const rotatePageDimensions = (w: number, h: number, rotate: number) => {
  const normalizedRotation = normalizeRotation(rotate);

  switch (normalizedRotation) {
    case 90:
      return {
        width: h,
        height: w,
      };
    case 270: // Rotate 270 degrees clockwise
      return {
        width: h,
        height: w,
      };
    default: // No rotation (0 degrees)
      return { width: w, height: h };
  }
};

export const PdfJSTextItemtoTextItem = (
  textItem: PDFJSTextItem,
  page: pdfjs.PDFPageProxy,
  embeddedFontsInfo: Record<string, any>, // eslint-disable-line @typescript-eslint/no-explicit-any
  fontMatches: FontMatchMap | null,
): EditableTextItem => {
  const { divStyle, textDivProperties } = calculateRenderedTextItemProps(textItem, page, {});

  const [, , , , , yTransform] = textItem.transform as number[];
  const { width: pageWidth, height: pageHeight } = page.getViewport({ scale: 1, rotation: 0 });

  const originalX = divStyle.left ?? 0;
  const fontSize = divStyle.fontSize ?? Math.abs(textItem.transform[3]);

  const pdfCoordinates = {
    x: originalX,
    y: yTransform,
    width: textItem.width,
    height: textItem.height,
    fontSize,
  };

  // Loader emits at scale=1 — canvas coords here are pdf-space pixels. The
  // SET_TEXT_ITEMS reducer rescales them to the current editor scale on insert
  // (and SET_SCALE handles any later zoom changes). Keeping canvas coords
  // populated keeps mergeAdjacentTextItems' canvas-side merge arithmetic
  // self-consistent — the reducer recomputes from originalPdfCoordinates
  // afterwards so any pre-scale values here are thrown away regardless.
  let canvasCoordinates: TextCoordinates = {
    x: originalX,
    y: divStyle.top ?? 0,
    width: textItem.width,
    height: textItem.height,
    fontSize,
  };

  if (page.rotate !== 0)
    canvasCoordinates = rotateCoordinates(canvasCoordinates, page.rotate, pageWidth, pageHeight, false);

  let fontName = textItem.fontName;

  const embeddedFont = embeddedFontsInfo[fontName] ?? {};
  let isEmbedded = true;

  // Server matching: items on a usable embedded face always keep it (the
  // pdf.js internal name doubles as the FontFace family) — the matched catalog
  // font and the fallback family sit behind it in the CSS stack, covering
  // missing glyphs per character (both read from the documentFonts registry).
  // Only unusable faces are moved to a catalog font here. Until the matches
  // arrive, items keep the raw internal name and are retroactively resolved by
  // REMAP_TEXT_ITEM_FONTS.
  if (fontMatches) {
    const resolved = resolveEmbeddedItemFont(fontMatches, fontName, embeddedFont);
    if (!resolved.useEmbeddedFace) {
      fontName = resolved.fontId;
      isEmbedded = false;
    }
  }

  // Set by useClientTextLoader from describeEmbeddedFont; undefined only for
  // fonts the loader never saw (embeddedFont fell back to {} above).
  const isBold = embeddedFont.isBold;
  const isItalic = embeddedFont.isItalic;

  const rgbFillColor = textItem.fillColor
    ? { r: textItem.fillColor[0], g: textItem.fillColor[1], b: textItem.fillColor[2] }
    : null;

  return {
    id: uuidv4(),
    originalPdfCoordinates: pdfCoordinates,
    currentPdfCoordinates: pdfCoordinates,
    originalCanvasCoordinates: canvasCoordinates,
    currentCanvasCoordinates: canvasCoordinates,
    text: textItem.str,
    originalText: textItem.str,
    // Items are emitted at scale=1 — the reducer rescales on insert. Carrying
    // a per-item scale here is a legacy artifact; its consumers are commented
    // out. Kept on the type for now, hard-coded to 1.
    scaleX: 1,
    scaleY: 1,
    isEdited: false,
    isDeleted: false,
    isDuplicated: false,
    isNew: false,
    isBold,
    isItalic,
    originalFontName: textItem.fontName,
    fontName,
    // PostScript name of the embedded face — what the save payload sends for
    // embedded items (the server's extracted font map is keyed by it).
    psFontName: embeddedFont.name as string | undefined,
    isEmbedded,
    isDirty: false,
    angle: Math.round((textDivProperties.angle ?? 0) + normalizeRotation(page.rotate)),
    renderingMode: textItem.renderingMode,
    ...(rgbFillColor ? { fillColor: rgbToHex(rgbFillColor) } : {}),
    ...(textItem.strokeColor ? { strokeColor: textItem.strokeColor } : {}),
  } as EditableTextItem;
};

const compareFonts = (item1: EditableTextItem, item2: EditableTextItem) => {
  return item1.fontName === item2.fontName && item1.isBold === item2.isBold && item1.isItalic === item2.isItalic;
};

const mergeAdjacentTextItems = (
  textItemsLine: EditableTextItem[],
  X_THRESHOLD: number,
  SPACE_WIDTH_MULTIPLIER: number = 1,
) => {
  const sortedLineByX = [...textItemsLine].sort((a, b) => a.currentPdfCoordinates.x - b.currentPdfCoordinates.x);
  const mergedTextItems = sortedLineByX.reduce((acc: EditableTextItem[], item: EditableTextItem) => {
    if (acc.length === 0) {
      acc = [item];
      return acc;
    }

    const lastItem = acc[acc.length - 1];

    const { x: itemX, width: itemW, fontSize: itemFontSize } = item.currentPdfCoordinates;
    const { x: lastItemX, width: lastItemW } = lastItem.currentPdfCoordinates;

    const xDiff = itemX - (lastItemX + lastItemW);

    if (xDiff < X_THRESHOLD * itemFontSize) {
      lastItem.text += xDiff > SPACE_WIDTH_MULTIPLIER * itemFontSize ? ' ' + item.text : item.text;
      lastItem.originalText = lastItem.text;

      lastItem.currentPdfCoordinates.width += itemW + (itemX - (lastItemX + lastItemW));

      lastItem.currentCanvasCoordinates.width +=
        item.currentCanvasCoordinates.width +
        (item.currentCanvasCoordinates.x -
          (lastItem.currentCanvasCoordinates.x + lastItem.currentCanvasCoordinates.width));

      lastItem.originalCanvasCoordinates.width = lastItem.currentCanvasCoordinates.width;
      lastItem.originalPdfCoordinates.width = lastItem.currentPdfCoordinates.width;
    } else {
      acc.push(item);
    }

    return acc;
  }, []);

  return mergedTextItems;
};

export const groupTextItemsIntoLines = (
  pageTextItems: PageTextItems,
  embeddedFontsMetadata: Record<string, any>, // eslint-disable-line @typescript-eslint/no-explicit-any
): PageTextItems => {
  const X_THRESHOLD = 0.6;
  const SPACE_WIDTH_MULTIPLIER = 0.2;
  const Y_THRESHOLD = 1;

  const newPageTextItems: PageTextItems = {};

  Object.keys(pageTextItems).map((pageNumber) => {
    const pageNumberInt = parseInt(pageNumber);
    let line: EditableTextItem[] = [];

    const sortedTextItemsByY = [...pageTextItems[pageNumberInt]].sort(
      (a, b) => b.currentPdfCoordinates.y - a.currentPdfCoordinates.y,
    );
    let newTextItems: EditableTextItem[] = [];

    sortedTextItemsByY.forEach((currItem) => {
      const item = { ...currItem };
      if (line.length === 0) {
        line.push(item);
        return;
      }

      const lastItem = line[line.length - 1];
      const yBottomLeftDiff = lastItem.currentPdfCoordinates.y - item.currentPdfCoordinates.y;

      if (
        yBottomLeftDiff < Y_THRESHOLD &&
        lastItem.fontName in embeddedFontsMetadata &&
        item.fontName in embeddedFontsMetadata &&
        embeddedFontsMetadata[lastItem.fontName].fontFamily === embeddedFontsMetadata[item.fontName].fontFamily &&
        // The family key is weight/style-agnostic (describeEmbeddedFont strips
        // "bold"/"italic"), so "Tahoma" and "Tahoma,Bold" share a key. Guard on
        // the style flags too — without this the collapse rewrites a bold item
        // onto a same-family regular subset (g_d0_f6 → g_d0_f3), dropping its
        // real bold face. Only genuine same-style subset splits collapse.
        embeddedFontsMetadata[lastItem.fontName].isBold === embeddedFontsMetadata[item.fontName].isBold &&
        embeddedFontsMetadata[lastItem.fontName].isItalic === embeddedFontsMetadata[item.fontName].isItalic
      ) {
        item.fontName = lastItem.fontName;
      }

      if (
        lastItem.angle === 0 &&
        item.angle === 0 &&
        yBottomLeftDiff < Y_THRESHOLD &&
        compareFonts(lastItem, item) &&
        lastItem.currentPdfCoordinates.fontSize === item.currentPdfCoordinates.fontSize &&
        (!item.fillColor || item.fillColor === lastItem.fillColor)
      ) {
        line.push(item);
      } else {
        if (line.length === 1) {
          newTextItems = [...newTextItems, ...line];
          line = [];
          line.push(item);
        } else {
          const mergedTextItems = mergeAdjacentTextItems(line, X_THRESHOLD, SPACE_WIDTH_MULTIPLIER);
          newTextItems = [...newTextItems, ...mergedTextItems];

          line = [];
          line.push(item);
        }
      }
    });

    if (line.length > 0) {
      const mergedTextItems = mergeAdjacentTextItems(line, X_THRESHOLD, SPACE_WIDTH_MULTIPLIER);
      newTextItems = [...newTextItems, ...mergedTextItems];
    }

    newPageTextItems[pageNumberInt] = newTextItems;
  });

  return newPageTextItems;
};

const DEGREES = 90;

export const rotateImageItem = (imageItem: ImageItem, page: PageMeta, scale: number) => {
  const actualRotation = DEGREES % 360;
  return new Promise<ImageItem>((resolve, reject) => {
    rotateImage(imageItem.url, actualRotation)
      .then((url) => {
        URL.revokeObjectURL(imageItem.url);
        const finalRotation = ((imageItem.rotation ?? 0) + actualRotation) % 360;
        const newCanvasCoordinates = {
          ...imageItem.currentCanvasCoordinates,
          width: imageItem.currentCanvasCoordinates.height,
          height: imageItem.currentCanvasCoordinates.width,
        };
        resolve({
          ...imageItem,
          url,
          rotation: finalRotation,
          currentCanvasCoordinates: newCanvasCoordinates,
          currentPdfCoordinates: canvasCoordinatesToPdfCoordinates(
            {
              ...newCanvasCoordinates,
              y: newCanvasCoordinates.y + newCanvasCoordinates.height,
            },
            page,
            scale,
          ),
        });
      })
      .catch(reject);
  });
};

export const compareCoordinates = (c1: Coordinates, c2: Coordinates) => {
  return (
    Math.abs(c1.x - c2.x) < COORDINATES_PIXELS_EPSILON &&
    Math.abs(c1.y - c2.y) < COORDINATES_PIXELS_EPSILON &&
    Math.abs(c1.width - c2.width) < COORDINATES_PIXELS_EPSILON &&
    Math.abs(c1.height - c2.height) < COORDINATES_PIXELS_EPSILON
  );
};

export const coordinatesIntersection = (c1: Coordinates, c2: Coordinates) => {
  // return intersection percentage
  const x_overlap = Math.max(0, Math.min(c1.x + c1.width, c2.x + c2.width) - Math.max(c1.x, c2.x));
  const y_overlap = Math.max(0, Math.min(c1.y + c1.height, c2.y + c2.height) - Math.max(c1.y, c2.y));
  const overlapArea = x_overlap * y_overlap;
  const area1 = c1.width * c1.height;
  const area2 = c2.width * c2.height;

  return overlapArea / Math.min(area1, area2);
};

// The baselineRatio default is a rough approximation of where the first-line
// baseline sits inside a line-height:1 box. The real value is font-dependent,
// so callers that know the rendered font stack should pass a measured ratio
// from fonts/baselineRatio.ts instead of relying on the default.
const canvasCoordinatesToTextItemCoordinates = (
  canvasCoordinates: TextCoordinates,
  angleDeg: number,
  baselineRatio: number = FALLBACK_BASELINE_RATIO,
) => {
  const angleRad = (angleDeg * Math.PI) / 180;

  const fontAscent = canvasCoordinates.fontSize * baselineRatio;

  return {
    ...canvasCoordinates,
    x: canvasCoordinates.x + fontAscent * Math.sin(angleRad),
    y: canvasCoordinates.y - fontAscent * Math.cos(angleRad),
  };
};

const textItemCoordinatesToCanvasCoordinates = (
  textItemCoordinates: Omit<TextCoordinates, 'width' | 'height'>,
  angleDeg: number,
  baselineRatio: number = FALLBACK_BASELINE_RATIO,
) => {
  const angleRad = (angleDeg * Math.PI) / 180;

  const fontAscent = textItemCoordinates.fontSize * baselineRatio;

  return {
    ...textItemCoordinates,
    x: textItemCoordinates.x - fontAscent * Math.sin(angleRad),
    y: textItemCoordinates.y + fontAscent * Math.cos(angleRad),
  };
};

/* ----------------------------------------------- Rotations ----------------------------------------------- */

export function rotateRectangleAroundPoint(
  coordinates: Coordinates,
  centerX: number,
  centerY: number,
  angleDegrees: number,
): { x: number; y: number; width: number; height: number } {
  const { x, y, width, height } = coordinates;

  // Normalize angle to [0, 360)
  const normalizedAngle = ((angleDegrees % 360) + 360) % 360;

  // Convert angle to radians
  const angleRadians = (normalizedAngle * Math.PI) / 180;

  // Translate the rectangle's top-left corner to the origin
  const translatedX = x - centerX;
  const translatedY = y - centerY;

  // Calculate the four corners of the rectangle
  const corners = [
    { x: translatedX, y: translatedY }, // Top-left
    { x: translatedX + width, y: translatedY }, // Top-right
    { x: translatedX, y: translatedY + height }, // Bottom-left
    { x: translatedX + width, y: translatedY + height }, // Bottom-right
  ];

  // Rotate all corners
  const rotatedCorners = corners.map((corner) => ({
    x: corner.x * Math.cos(angleRadians) - corner.y * Math.sin(angleRadians) + centerX,
    y: corner.x * Math.sin(angleRadians) + corner.y * Math.cos(angleRadians) + centerY,
  }));

  // Find the new bounding rectangle
  const minX = Math.min(...rotatedCorners.map((corner) => corner.x));
  const minY = Math.min(...rotatedCorners.map((corner) => corner.y));
  const maxX = Math.max(...rotatedCorners.map((corner) => corner.x));
  const maxY = Math.max(...rotatedCorners.map((corner) => corner.y));

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

function rotatePoint(point: Point, rotate: number, pageWidth: number, pageHeight: number): Point {
  const { x, y } = point;

  switch (rotate) {
    case 90: // Rotate 90 degrees clockwise
      return {
        x: pageHeight - y,
        y: x,
      };
    case 180: // Rotate 180 degrees clockwise
      return {
        x: pageWidth - x,
        y: pageHeight - y,
      };
    case 270: // Rotate 270 degrees clockwise
      return {
        x: y,
        y: pageWidth - x,
      };
    default: // No rotation (0 degrees)
      return point;
  }
}

function rotateCoordinates(
  coordinates: TextCoordinates,
  pageRotation: number,
  pageWidth: number,
  pageHeight: number,
  adjustForBoundingBox?: boolean,
  flipBoxDimensions?: boolean,
): TextCoordinates;
function rotateCoordinates(
  coordinates: Coordinates,
  pageRotation: number,
  pageWidth: number,
  pageHeight: number,
  adjustForBoundingBox?: boolean,
  flipBoxDimensions?: boolean,
): Coordinates;

function rotateCoordinates(
  coordinates: Coordinates | TextCoordinates,
  pageRotation: number,
  pageWidth: number,
  pageHeight: number,
  adjustForBoundingBox: boolean = true,
  flipBoxDimensions: boolean = true,
): Coordinates | TextCoordinates {
  const rotation = normalizeRotation(pageRotation);
  const { x, y, width, height } = coordinates;
  const a = adjustForBoundingBox ? 1 : 0;

  switch (rotation) {
    case 90: // Rotate 90 degrees clockwise
      return {
        ...coordinates,
        x: pageHeight - y - height * a,
        y: x,
        ...(flipBoxDimensions
          ? {
              width: height,
              height: width,
            }
          : {}),
      };
    case 180: // Rotate 180 degrees clockwise
      return {
        ...coordinates,
        x: pageWidth - x - width * a,
        y: pageHeight - y - height * a,
      };
    case 270: // Rotate 270 degrees clockwise
      return {
        ...coordinates,
        x: y,
        y: pageWidth - x - width * a,
        ...(flipBoxDimensions
          ? {
              width: height,
              height: width,
            }
          : {}),
      };
    default: // No rotation (0 degrees)
      return coordinates;
  }
}

function rotateItem(
  textItem: EditableTextItem,
  rotate: number,
  pageWidth: number,
  pageHeight: number,
): EditableTextItem;

function rotateItem(textItem: FormField, rotate: number, pageWidth: number, pageHeight: number): FormField;
function rotateItem(textItem: Shape, rotate: number, pageWidth: number, pageHeight: number): Shape;
function rotateItem(
  textItem: ImageItem | Annotation,
  rotate: number,
  pageWidth: number,
  pageHeight: number,
): ImageItem | Annotation;

function rotateItem(
  item: EditableTextItem | Shape | ImageItem | Annotation | FormField,
  rotate: number,
  pageWidth: number,
  pageHeight: number,
): EditableTextItem | Shape | ImageItem | Annotation | FormField {
  const rotation = ((rotate % 360) + 360) % 360;

  if ('originalPdfCoordinates' in item) {
    // Text Items
    return {
      ...item,
      originalPdfCoordinates: rotateCoordinates(item.originalPdfCoordinates, rotation, pageWidth, pageHeight, false),
      currentPdfCoordinates: rotateCoordinates(item.currentPdfCoordinates, rotation, pageWidth, pageHeight, false),
      originalCanvasCoordinates: rotateCoordinates(
        item.originalCanvasCoordinates,
        rotation,
        pageWidth,
        pageHeight,
        false,
      ),
      currentCanvasCoordinates: rotateCoordinates(
        item.currentCanvasCoordinates,
        rotation,
        pageWidth,
        pageHeight,
        false,
      ),
      angle: -(item.angle + rotation) % 360,
    };
  } else if (!('originalPdfCoordinates' in item) && 'currentPdfCoordinates' in item) {
    // Image Item OR Annotation
    return {
      ...item,
      currentPdfCoordinates: rotateCoordinates(item.currentPdfCoordinates, rotation, pageWidth, pageHeight),
      currentCanvasCoordinates: rotateCoordinates(item.currentCanvasCoordinates, rotation, pageWidth, pageHeight),
      rotation: -((item.rotation ?? 0) + rotation) % 360,
    };
  } else if ('pdfPoints' in item) {
    // Shapes
    let newPdfPoints = item.pdfPoints.map((pt) => rotatePoint(pt, rotation, pageWidth, pageHeight));
    let newCanvasPoints = item.canvasPoints.map((pt) => rotatePoint(pt, rotation, pageWidth, pageHeight));
    const length = newPdfPoints.length;

    switch (rotation) {
      case 90:
        // Rotate order clockwise
        newPdfPoints = newPdfPoints.map((_, index) => newPdfPoints[(length - index - 1 + 1) % length]);
        newCanvasPoints = newCanvasPoints.map((_, index) => newCanvasPoints[(length - index - 1 + 1) % length]);
        break;
      case 180:
        // Reverse the order
        newPdfPoints = [...newPdfPoints].reverse();
        newCanvasPoints = [...newCanvasPoints].reverse();
        break;
      case 270:
        // Rotate order counterclockwise
        newPdfPoints = newPdfPoints.map((_, index) => newPdfPoints[(index + 1) % length]);
        newCanvasPoints = newCanvasPoints.map((_, index) => newCanvasPoints[(index + 1) % length]);
        break;
    }

    return {
      ...item,
      pdfPoints: newPdfPoints,
      canvasPoints: newCanvasPoints,
    };
  } else if ('canvasCoordinates' in item) {
    // Form field
    return {
      ...item,
      pdfCoordinates: rotateCoordinates(item.pdfCoordinates, rotation, pageWidth, pageHeight, false),
      canvasCoordinates: rotateCoordinates(item.canvasCoordinates, rotation, pageWidth, pageHeight, false),
    };
  }

  return item;
}

type BoundingBox = {
  left: number;
  bottom: number;
  top: number;
  right: number;
};

function rotateBoundingBox(
  boundingBox: BoundingBox,
  rotation: number,
  pageWidth: number,
  pageHeight: number,
): BoundingBox {
  const { left, bottom, top, right } = boundingBox;

  switch (rotation) {
    case 90:
      return {
        left: pageHeight - top,
        bottom: left,
        right: pageHeight - bottom,
        top: right,
      };

    case 180:
      return {
        left: pageWidth - right,
        bottom: pageHeight - top,
        right: pageWidth - left,
        top: pageHeight - bottom,
      };

    case 270:
      return {
        left: bottom,
        bottom: pageWidth - right,
        right: top,
        top: pageWidth - left,
      };

    case 0:
    default:
      // No rotation, return the bounding box as is
      return boundingBox;
  }
}

function annotationPathsToPdfPoints(
  paths: Paths,
  canvasOrigin: { x: number; y: number },
  page: PageMeta,
  scale: number,
): { x: number; y: number }[][] {
  const { height: pdfPageHeight, viewBox } = page.getViewport({ scale: 1, rotation: 0 });
  const pageYOffset = viewBox[1];
  const { scaleX, scaleY } = getCanvasScales(page, scale);
  const canvasViewport = page.getViewport({ scale });

  return paths.map((stroke) =>
    stroke.map((pt) => {
      let cx = canvasOrigin.x + pt.x;
      let cy = canvasOrigin.y + pt.y;

      if (page.rotate !== 0) {
        const rotated = rotateCoordinates(
          { x: cx, y: cy, width: 0, height: 0 },
          -page.rotate,
          canvasViewport.width,
          canvasViewport.height,
          false,
          false,
        );
        cx = rotated.x;
        cy = rotated.y;
      }

      return {
        x: cx / scaleX,
        y: pdfPageHeight - cy / scaleY + pageYOffset,
      };
    }),
  );
}

export {
  annotationPathsToPdfPoints,
  canvasCoordinatesToPdfCoordinates,
  canvasPointsToPdfPoints,
  pdfCoordinatesToCanvasCoordinates,
  pdfPointsToCanvasPoints,
  scaleTextBoxItemToCanvas,
  canvasCoordinatesToTextItemCoordinates,
  textItemCoordinatesToCanvasCoordinates,
  rotateCoordinates,
  rotateItem,
  rotateBoundingBox,
};
